// noinspection JSIgnoredPromiseFromCall

import { useEffect, useState, useRef } from 'react';
import apiFetch from '@/services/apiFetch';
import EndPointTypes from 'interfaces/EndPointTypes';
import { AuthProtect } from '@/common/enum';

const useNotification = ({ groupId, selectedIncidents, joinRoom, onConnect }) => {
  const [currentNotifications, setCurrentNotifications] = useState<DashboardNotification[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initializedRef = useRef(false);
  const groupIdRef = useRef<number | null>(null);
  const selectedIncidentsRef = useRef<number[]>([]);

  const fetchCurrentNotifications = (
    groupId: number,
    selectedIncidents: number[]
  ): EndPointTypes => ({
    authorization: AuthProtect.REQUIRED,
    method: 'GET',
    url: 'api/notifications/current',
    params: {
      pvGroupID: groupId,
      pvIncidentID: selectedIncidents,
    },
    defaultBaseURL: 'https://oh9z0ztiy6.execute-api.us-east-1.amazonaws.com/prod/',
  });

  const clearExistingTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const scheduleNextRefetch = (notifications: INotification[]) => {
    const now = new Date().getTime();

    const expiringSoonest = notifications
      .filter((n) => n.pvPersistant !== 1 && n.pvExpireDate)
      .map((n) => new Date(n.pvExpireDate).getTime() - now)
      .filter((diff) => diff > 0)
      .sort((a, b) => a - b)[0];

    if (expiringSoonest) {
      clearExistingTimeout();
      timeoutRef.current = setTimeout(() => {
        refetch();
      }, expiringSoonest);
    }
  };

  const refetch = async () => {
    const gid = groupIdRef.current;
    const incidents = selectedIncidentsRef.current;
    if (!gid || !incidents?.length) return;
    const endpoint = fetchCurrentNotifications(gid, incidents);
    const res = await apiFetch<{ dataset: INotification[] }>(endpoint).then(
      (res) => res.data.dataset ?? []
    );
    setCurrentNotifications((prev: DashboardNotification[]): DashboardNotification[] => {
      const now = new Date().getTime();

      const updated: DashboardNotification[] = res.map((item) => {
        const prevItem = prev.find((p) => p.pvDataID === item.pvDataID);

        if (!prevItem) {
          return { ...item, showToast: true };
        }

        if (item.pvPersistant === 1) {
          return { ...item, showToast: false };
        }

        const prevExpire = new Date(prevItem.pvExpireDate).getTime();
        const currExpire = new Date(item.pvExpireDate).getTime();

        const isUpdated = currExpire > now && currExpire !== prevExpire;

        return {
          ...item,
          showToast: isUpdated,
        };
      });

      return JSON.stringify(prev) !== JSON.stringify(updated) ? updated : prev;
    });
    scheduleNextRefetch(res);
  };

  useEffect(() => {
    groupIdRef.current = groupId;
    selectedIncidentsRef.current = selectedIncidents;
  }, [groupId, selectedIncidents]);

  useEffect(() => {
    refetch();
    if (joinRoom && !initializedRef.current && onConnect) {
      onConnect(() => {
        joinRoom('notifications', (_data: any) => {
          refetch();
        });
        initializedRef.current = true;
      });
    }

    return () => clearExistingTimeout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, selectedIncidents, joinRoom, onConnect]);

  return { currentNotifications };
};

export default useNotification;
